import React from 'react';
import { useScreenPerfMonitor } from './use-screen-perf-monitor';
import { ScreenPerfContext } from './screen-perf-context.context';

export { ScreenPerfContext } from './screen-perf-context.context';

export function ScreenPerfProvider({ screenName, children }: { screenName: string; children: React.ReactNode }) {
  const { contextValue } = useScreenPerfMonitor(screenName);

  return <ScreenPerfContext.Provider value={contextValue}>{children}</ScreenPerfContext.Provider>;
}

export function useRegisterLoading(isLoading: boolean, label = 'anonymous') {
  const ctx = React.useContext(ScreenPerfContext);

  const id = `${label}-${React.useId()}`;
  const isLoadingRef = React.useRef(isLoading);
  const previousIsLoadingRef = React.useRef(isLoading);

  React.useEffect(() => {
    const loadingChanged = previousIsLoadingRef.current !== isLoading;
    isLoadingRef.current = isLoading;
    previousIsLoadingRef.current = isLoading;

    // Registration schedules the initial check, so only notify for real state transitions.
    if (ctx && loadingChanged) ctx.notifyLoadingChange();
  }, [ctx, isLoading]);

  React.useEffect(() => {
    if (!ctx) return;
    const unregister = ctx.registerLoading(id, () => isLoadingRef.current);
    return () => unregister();
  }, [ctx, id]);
}

export function useScreenPerf() {
  const ctx = React.useContext(ScreenPerfContext);
  if (!ctx) throw new Error('useScreenPerf must be used within a ScreenPerfProvider');
  return ctx;
}
